import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, FileText, MessageCircle, Share2, Printer, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, StatCard, TableWrap, Empty, th, td, tr } from "@/components/common";
import { waPhone } from "@/components/market";
import { download } from "@/components/gst";
import { PartyTds } from "@/components/tds";

function PartyShare({ pid, range }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get(`/parties/${pid}/statement-share`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); }, [pid]);
  const [busy, setBusy] = useState(false);
  if (!info) return null;
  const sharePdf = async () => {
    setBusy(true);
    try {
      const r = await fetch(info.pdf_url);
      if (!r.ok) throw new Error("PDF not available");
      const file = new File([await r.blob()], info.file_name, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: info.message });
      else { const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = info.file_name; a.click(); toast.info("PDF downloaded — attach it in WhatsApp"); }
    } catch (e) { if (e.name !== "AbortError") toast.error(e.message || "Share failed"); } finally { setBusy(false); }
  };
  const btn = "flex h-9 w-9 items-center justify-center rounded-lg border bg-card transition-colors hover:bg-secondary disabled:opacity-50";
  return (
    <div className="flex items-center gap-1.5" data-testid="party-share-card">
      <a title={info.phone ? `WhatsApp to ${info.phone}` : "WhatsApp (number save nahi hai)"} data-testid="party-share-whatsapp-btn" href={`https://wa.me/${waPhone(info.phone)}?text=${encodeURIComponent(info.message)}`} target="_blank" rel="noreferrer" className={`${btn} border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`}><MessageCircle className="h-4 w-4" /></a>
      <button type="button" title="Share PDF" data-testid="party-share-pdf-share-btn" disabled={busy} onClick={sharePdf} className={btn}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}</button>
      <a title="Print" data-testid="party-share-pdf-open-btn" href={info.pdf_url} target="_blank" rel="noreferrer" className={btn}><Printer className="h-4 w-4" /></a>
      <button type="button" title="Download PDF" data-testid="party-statement-pdf-btn" onClick={() => download(`/parties/${pid}/statement.pdf`, range, info.file_name)} className={btn}><FileDown className="h-4 w-4 text-rose-600" /></button>
      <button type="button" title="Download Excel" data-testid="party-statement-xlsx-btn" onClick={() => download(`/parties/${pid}/statement.xlsx`, range, info.file_name.replace(".pdf", ".xlsx"))} className={btn}><FileSpreadsheet className="h-4 w-4 text-emerald-700" /></button>
    </div>
  );
}

function sliceRows(rows, from, to) {
  if (!from && !to) return rows;
  const before = rows.filter((r) => from && (r.date || "") < from);
  const keep = rows.filter((r) => (!from || (r.date || "") >= from) && (!to || (r.date || "") <= to));
  const ob = before.length ? before[before.length - 1].balance : 0;
  return [...(from ? [{ date: from, particulars: "Opening Balance (b/f)", debit: ob > 0 ? ob : 0, credit: ob < 0 ? -ob : 0, balance: ob }] : []), ...keep];
}

function PartyStatement({ rows: all, pid }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const rows = sliceRows(all, from, to);
  const range = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
  const di = "h-9 rounded-lg border bg-card px-2 text-sm";
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Party Statement</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" title="From" data-testid="party-statement-from" className={di} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-xs text-muted-foreground">to</span>
          <input type="date" title="To" data-testid="party-statement-to" className={di} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          {(from || to) && <button type="button" data-testid="party-statement-clear" onClick={() => { setFrom(""); setTo(""); }} className="text-xs font-semibold text-primary">Clear</button>}
          <PartyShare pid={pid} range={range} />
        </div>
      </div>
      <TableWrap>
        <thead><tr>{["Date", "Particulars", "Debit (+)", "Credit (−)", "Balance"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody data-testid="party-statement">
          {rows.map((r, i) => (
            <tr key={i} className={tr} data-testid={`party-statement-row-${i}`}>
              <td className={td}>{r.date ? fmtDate(r.date) : "—"}</td>
              <td className={td}>{r.particulars}</td>
              <td className={`${td} amount`}>{r.debit ? inr(r.debit, 2) : ""}</td>
              <td className={`${td} amount text-emerald-600`}>{r.credit ? inr(r.credit, 2) : ""}</td>
              <td className={`${td} amount font-semibold ${r.balance > 0 ? "text-rose-600" : ""}`}>{inr(r.balance, 2)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {rows.length === 0 && <Empty testid="party-statement-empty" text="No entries yet" />}
    </>
  );
}

export default function PartyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get(`/parties/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/parties"); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { party: p, stats: s, bookings, is_admin: isAdmin, statement } = d;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <button data-testid="party-back-btn" onClick={() => navigate(-1)} className="mb-2 flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="party-profile-name">{p.name}</h1>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {p.contact_person && <span>{p.contact_person}</span>}
            {p.phone && <a href={`tel:${p.phone}`} data-testid="party-phone-link" className="flex items-center gap-1 text-primary hover:underline"><Phone className="h-3.5 w-3.5" />{p.phone}</a>}
            {(p.address || p.city) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[p.address, p.city, p.state].filter(Boolean).join(", ")}</span>}
          </div>
          <div className="mt-1 flex gap-4 font-mono text-xs text-muted-foreground">
            {p.gstin && <span data-testid="party-gstin">GSTIN {p.gstin}</span>}
            {p.pan && <span>PAN {p.pan}</span>}
          </div>
        </div>
        <Button variant="outline" asChild><Link to="/bookings/new" data-testid="party-new-booking-btn"><FileText className="mr-1 h-4 w-4" />New Booking</Link></Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
        <StatCard testid="party-total-bookings" label="Total Bookings" value={s.total_bookings} hint={`${s.as_consignor} as sender · ${s.as_consignee} as receiver`} />
        <StatCard testid="party-total-freight" label="Total Freight" value={inr(s.total_freight)} delay={50} />
        {isAdmin && <StatCard testid="party-received" label="Received" value={inr(s.received)} hint={`of ${inr(s.total_bill)} billed`} accent="text-emerald-600" delay={100} />}
        {(isAdmin || statement) && <StatCard testid="party-outstanding" label="Pending Dues" value={inr(s.outstanding)} hint={s.opening ? `incl. opening ${inr(s.opening)}` : undefined} accent={s.outstanding > 0 ? "text-rose-600" : "text-emerald-600"} delay={150} />}
      </div>

      {statement && (
        <div className="mt-8 min-w-0"><PartyStatement rows={statement} pid={id} /></div>
      )}
      {statement && <PartyTds pid={id} bookings={bookings.filter((b) => b.billed)} onSaved={load} />}

      <h2 className="mb-3 mt-8 text-xl sm:text-2xl font-semibold tracking-tight">Booking & Freight History</h2>
      <TableWrap>
        <thead><tr>{["LR No", "Date", "Route", "Material", "Role", "Vehicle", "Freight", ...(isAdmin ? ["Billed", "Received", "Due"] : []), "Status"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {bookings.map((b) => (
            <tr key={b.id} className={`${tr} cursor-pointer`} onClick={() => navigate(`/bookings/${b.id}`)} data-testid={`party-booking-${b.lr_number}`}>
              <td className={`${td} font-mono font-semibold text-primary`}>{b.lr_number}</td>
              <td className={td}>{fmtDate(b.booking_date)}</td>
              <td className={td}>{b.pickup_location} → {b.drop_location}</td>
              <td className={`${td} text-muted-foreground`}>{b.material_type}</td>
              <td className={td}><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${b.side === "Billed Party" ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>{b.side}</span></td>
              <td className={`${td} font-mono text-xs`}>{b.vehicle_number || "-"}</td>
              <td className={`${td} amount`}>{inr(b.freight_amount)}</td>
              {isAdmin && <td className={`${td} amount`}>{inr(b.total_bill)}</td>}
              {isAdmin && <td className={`${td} amount text-emerald-600`}>{inr(b.received)}</td>}
              {isAdmin && <td className={`${td} amount font-semibold ${b.outstanding > 0 ? "text-rose-600" : ""}`} data-testid={`party-due-${b.lr_number}`}>{b.side === "Billed Party" ? inr(b.outstanding) : "—"}</td>}
              <td className={td}><StatusBadge status={b.status} /></td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {bookings.length === 0 && <Empty testid="party-bookings-empty" text="No bookings yet for this party" />}
    </div>
  );
}
